import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { BEAUFORT } from '../../data/treatmentUi.ts'
import { unitSymbol } from '../../data/catalog.ts'
import { fmtNum } from '../../lib/format.ts'
import { Banner, Pill } from '../../ui/components.tsx'

/** M15-C: what every treatment screen shares (docs/M15.md C, rules 4, 6). The legal banner stays on every treatment screen until the owner removes it in a
 *  later change; a sand pill says what is missing or odd (no label, no safety data sheet, the wind above the printed limit, no verified plan) and never
 *  refuses; the wind rule is the law's printed sentence "(à vérifier)". */
export function LegalBanner({ testId = 'treatment-legal' }: { testId?: string }) {
  const { t } = useTranslation()
  return <Banner tone="warn" testId={testId}>{t('treatment.legalBanner')}</Banner>
}
export function SandPill({ children, testId }: { children: ReactNode; testId?: string }) {
  return <span data-testid={testId}><Pill tone="amber">{children}</Pill></span>
}
/** The rule printed beside the wind field: never computed, never a refusal. */
export function WindRule() {
  const { t } = useTranslation()
  return <p className="meta" data-testid="wind-rule">{t('treatment.windRule', { limit: BEAUFORT.limit })}</p>
}
/** "3 L/ha" as a person reads it. */
export const doseText = (value: number | string, unitCode: string, lang: string): string => `${fmtNum(Number(value), lang, 3)} ${unitSymbol(unitCode, lang)}`
export const qtyText = (value: number | string | null | undefined, unitCode: string | null | undefined, lang: string): string => (value == null || !unitCode ? '—' : `${fmtNum(Number(value), lang, 3)} ${unitSymbol(unitCode, lang)}`)
/** The flag words of the register (the server's soft codes, shown as they came; each is a review item for the office). STOCK_NEGATIVE is the stock's word (A's
 *  code: the store went below zero with this treatment, an entry is missing), carried on the record since the M15a ergonomics review; the legal register's rows
 *  keep filtering it out (data/treatmentUi.ts registerRows), the job page and the apply history print it. */
export const FLAG_KEY: Record<string, string> = {
  UNPLANNED_APPLICATION: 'treatment.flag.UNPLANNED_APPLICATION', OPERATOR_NOT_CERTIFIED: 'treatment.flag.OPERATOR_NOT_CERTIFIED', SPRAY_CERTIFICATE_TYPE_UNDEFINED: 'treatment.flag.SPRAY_CERTIFICATE_TYPE_UNDEFINED',
  PLAN_PRODUCT_MISMATCH: 'treatment.flag.PLAN_PRODUCT_MISMATCH', WIND_ABOVE_LIMIT: 'treatment.flag.WIND_ABOVE_LIMIT', STOCK_NEGATIVE: 'treatment.flag.STOCK_NEGATIVE',
}
export function FlagPills({ codes, testId }: { codes: readonly string[]; testId?: string }) {
  const { t } = useTranslation()
  if (codes.length === 0) return null
  return <span className="flex flex-wrap gap-1.5" data-testid={testId}>{codes.map((c) => <SandPill key={c}>{FLAG_KEY[c] ? t(FLAG_KEY[c]) : c}</SandPill>)}</span>
}
